import type { PanelItem } from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import { useEffect, useState } from 'react'
import type { SwipeEventData } from 'react-swipeable'
import { useSwipeable } from 'react-swipeable'
import { useContext } from 'use-context-selector'

import Node from 'components/Editor/cource/layout/Node'
import type { Block } from 'services/Store/Project/types'

import SliderContext from '../SliderContext'
import * as s from './SlidesList.module.scss'

interface ISlideProps {
  data: PanelItem
  block: Block
  index: number
  isV2?: boolean
}

interface ISlidesListProps {
  block: Block
  isV2?: boolean
}

const Slide = ({ data, block, index, isV2 }: ISlideProps) => {
  const { mode, rightArrowNoLoop, leftArrowNoLoop, prevSlide, nextSlide, items, slideIndex } =
    useContext(SliderContext)

  const onSwipedLeft = (e: SwipeEventData) => {
    e.event.preventDefault()
    e.event.stopPropagation()
    if (!rightArrowNoLoop) {
      nextSlide()
    }
  }

  const onSwipedRight = (e: SwipeEventData) => {
    e.event.preventDefault()
    e.event.stopPropagation()
    if (!leftArrowNoLoop) {
      prevSlide()
    }
  }

  const handlers = useSwipeable({
    onSwipedLeft,
    onSwipedRight,
  })

  return (
    <div
      aria-label={`Slide ${index + 1} of ${items.length}`}
      aria-roledescription='Slide'
      className={s.slide}
      {...handlers}
      aria-hidden={slideIndex !== index}
      {...{ inert: slideIndex !== index ? '' : undefined }} // inert attribute for better accessibility support (https://caniuse.com/mdn-html_global_attributes_inert) don't supported in React 18 only in React 19
      role='group'
    >
      <div className={cn(s.nodeWrapper, { [s.isV2]: isV2 })} key={data.value}>
        <Node block={block} id={data.value} mode={mode} isItem />
      </div>
    </div>
  )
}

const SlidesList = ({ block, isV2 }: ISlidesListProps) => {
  const { slideIndex, items } = useContext(SliderContext)
  const idsKey = items.map((item) => item.value).join('-')
  const [disableTransition, setDisableTransition] = useState(false)
  useEffect(() => {
    setDisableTransition(true)
    const timer = setTimeout(() => {
      setDisableTransition(false)
    }, 100)
    return () => clearTimeout(timer)
  }, [idsKey])
  return (
    <div className={cn(s.listWrapper, { [s.isV2]: isV2 })}>
      <div
        aria-live='polite'
        className={cn(s.list, { [s.isV2]: isV2 })}
        style={{
          transform: `translateX(-${slideIndex * 100}%)`,
          transition: disableTransition ? 'none' : 'transform 0.5s ease-in-out',
        }}
      >
        {items.map((slide: PanelItem, index: number) => (
          <Slide block={block} data={slide} index={index} isV2={isV2} key={slide.value} />
        ))}
      </div>
    </div>
  )
}

export default SlidesList
